import React, { useState, useCallback, useEffect, useRef } from 'react';
import Image from 'next/image';
import { Range } from 'react-range';
import { BreadcrumbMenu, MenuRoute, SubMenuRoute } from '@/types/menu';
import { useRouter } from 'next/navigation';
import { Manufacturer } from '@/types/product';
import { ProductFilters } from '@/types/product';

interface ProductSidebarProps {
  categories: MenuRoute[];
  breadcrumbMenu: BreadcrumbMenu;
  manufacturers: Manufacturer[];
  expandedCategories: string[];
  expandedSubcategories?: string[];
  onToggleCategory: (categoryName: string) => void;
  onToggleSubcategory?: (subcategoryId: string) => void;
  onFilterChange: (manufacturer?: number, price_min?: number, price_max?: number) => void;
  selectManufacturer: Manufacturer | null;
  selectPriceRange: [number, number];
}

const ProductSidebar: React.FC<ProductSidebarProps> = ({
  categories,
  breadcrumbMenu,
  expandedCategories,
  expandedSubcategories = [],
  onToggleCategory,
  onToggleSubcategory,
  onFilterChange,
  manufacturers,
  selectManufacturer,
  selectPriceRange,
}) => {
  const router = useRouter();
  const [searchBrand, setSearchBrand] = useState('');
  const [priceRange, setPriceRange] = useState(selectPriceRange || [0, 200]);
  const [minPrice, setMinPrice] = useState((selectPriceRange?.[0] || 0).toString());
  const [maxPrice, setMaxPrice] = useState((selectPriceRange?.[1] || 200).toString());

  // Estados para marcas
  const [filteredManufacturers, setFilteredManufacturers] = useState<Manufacturer[]>(manufacturers);
  const [selectedManufacturer, setSelectedManufacturer] = useState<Manufacturer | null>(selectManufacturer || null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Las sub-subcategorías expandidas ahora se reciben como prop desde CategoriesSidebar

  // Filtrar fabricantes cuando cambia la búsqueda
  useEffect(() => {
    if (searchBrand.trim() === '') {
      setFilteredManufacturers(manufacturers);
    } else {
      const filtered = manufacturers.filter((manufacturer) =>
        manufacturer.name.toLowerCase().includes(searchBrand.toLowerCase())
      );
      setFilteredManufacturers(filtered);
    }
  }, [searchBrand, manufacturers]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ProductSidebar solo usa las categorías que recibe por props
  const categoriesToUse = categories;

  // Funciones memoizadas para optimizar el rendimiento
  const handleCategoryClick = useCallback(
    (category: MenuRoute) => {
      // Navegar inmediatamente sin esperar el toggle
      if (category.url) {
        router.push(category.url);
      }
    },
    [router]
  );

  const handleSubcategoryClick = useCallback(
    (subcategory: SubMenuRoute) => {
      // Navegar inmediatamente
      if (subcategory.url) {
        router.push(subcategory.url);
      }
    },
    [router]
  );

  const handleSubSubcategoryClick = useCallback(
    (subSubcategory: SubMenuRoute) => {
      // Navegar inmediatamente
      if (subSubcategory.url) {
        router.push(subSubcategory.url);
      }
    },
    [router]
  );

  const handleCategoryToggle = useCallback(
    (categoryName: string) => {
      onToggleCategory(categoryName);
    },
    [onToggleCategory]
  );

  const handleSubcategoryToggle = useCallback(
    (subcategoryId: string) => {
      if (onToggleSubcategory) {
        onToggleSubcategory(subcategoryId);
      }
    },
    [onToggleSubcategory]
  );

  // Handler para seleccionar fabricante
  const handleManufacturerSelect = useCallback((manufacturer: Manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setSearchBrand(manufacturer.name);
    setIsDropdownOpen(false);
  }, []);

  // Handler para limpiar selección
  const handleClearManufacturer = useCallback(() => {
    setSelectedManufacturer(null);
    setSearchBrand('');
  }, []);

  // Handler para el botón de filtrar
  const handleSearchByFilters = useCallback(() => {
    // Aplicar los filtros usando la función del componente padre
    onFilterChange(selectedManufacturer?.id, priceRange[0], priceRange[1]);
  }, [selectedManufacturer, priceRange, onFilterChange]);

  // Sincronizar el rango de precios cuando cambie desde el componente padre
  useEffect(() => {
    if (selectPriceRange) {
      setPriceRange(selectPriceRange);
      setMinPrice(selectPriceRange[0].toString());
      setMaxPrice(selectPriceRange[1].toString());
    }
  }, [selectPriceRange]);

  // Sincronizar el fabricante seleccionado cuando cambie desde el componente padre
  useEffect(() => {
    if (selectManufacturer) {
      setSelectedManufacturer(selectManufacturer);
      setSearchBrand(selectManufacturer.name);
    }
  }, [selectManufacturer]);

  // Validar y actualizar precio mínimo
  const handleMinPriceChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value) || 0;
      const maxValue = priceRange[1];

      // Validar límites
      if (numValue < 0) {
        setMinPrice('0');
        setPriceRange([0, maxValue]);
      } else if (numValue > 200) {
        setMinPrice('200');
        setPriceRange([200, maxValue]);
      } else if (numValue > maxValue) {
        // Si el mínimo es mayor al máximo, intercambiar valores
        setMinPrice(value);
        setMaxPrice(numValue.toString());
        setPriceRange([numValue, numValue]);
      } else {
        setMinPrice(value);
        setPriceRange([numValue, maxValue]);
      }
    },
    [priceRange]
  );

  // Validar y actualizar precio máximo
  const handleMaxPriceChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value) || 0;
      const minValue = priceRange[0];

      // Validar límites
      if (numValue < 0) {
        setMaxPrice('0');
        setPriceRange([minValue, 0]);
      } else if (numValue > 200) {
        setMaxPrice('200');
        setPriceRange([minValue, 200]);
      } else if (numValue < minValue) {
        // Si el máximo es menor al mínimo, intercambiar valores
        setMaxPrice(value);
        setMinPrice(numValue.toString());
        setPriceRange([numValue, numValue]);
      } else {
        setMaxPrice(value);
        setPriceRange([minValue, numValue]);
      }
    },
    [priceRange]
  );

  // Función para determinar si una categoría está seleccionada
  const isCategorySelected = useCallback((category: MenuRoute) => {
    return breadcrumbMenu?.menuLevel1?.id === category.id;
  }, [breadcrumbMenu]);

  // Función para determinar si una subcategoría está seleccionada
  const isSubcategorySelected = useCallback((subcategory: SubMenuRoute) => {
    return breadcrumbMenu?.menuLevel2?.id === subcategory.id;
  }, [breadcrumbMenu]);

  // Función para determinar si una sub-subcategoría está seleccionada
  const isSubSubcategorySelected = useCallback((subSubcategory: SubMenuRoute) => {
    return breadcrumbMenu?.menuLevel3?.id === subSubcategory.id;
  }, [breadcrumbMenu]);

  // Verificar si hay filtros activos
  const hasActiveFilters =
    selectedManufacturer !== null || priceRange[0] !== 0 || priceRange[1] !== 40;

  return (
    <div data-component-id="src/components/common/ProductSidebar.tsx:232:4" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="232" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-%5B280px%5D%20bg-%5B%23FEF5E6%5D%20relative%22%7D" className="w-[280px] bg-[#FEF5E6] relative">
      {/* Contenido del sidebar */}
      {/* Categories */}
      <div data-component-id="src/components/common/ProductSidebar.tsx:235:6" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="235" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pr-4%20py-6%22%7D" className="pr-4 py-6">
        <div data-component-id="src/components/common/ProductSidebar.tsx:236:8" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="236" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          {categoriesToUse.map((category, index) => (
            <div data-component-id="src/components/common/ProductSidebar.tsx:238:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="238" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" key={category.name} className="relative">
              {/* Línea de color que se expande */}
              <div data-component-id="src/components/common/ProductSidebar.tsx:240:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="240" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`absolute left-0 top-0 w-1 transition-all duration-300 ${expandedCategories.includes(category.name) ? 'h-full' : 'h-5'
                  }`}
                style={{ backgroundColor: category.color }}
              />

              <div data-component-id="src/components/common/ProductSidebar.tsx:246:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="246" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20justify-between%20mb-2%20ml-4%22%7D" className="flex flex-row items-center justify-between mb-2 ml-4">
                <span data-component-id="src/components/common/ProductSidebar.tsx:247:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="247" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                  className={`font-bold text-base leading-5 uppercase tracking-wide cursor-pointer hover:text-global-2 transition-colors ${isCategorySelected(category) ? 'text-global-2' : 'text-global-1'
                    }`}
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                  }}
                  onClick={() => handleCategoryClick(category)}

                >
                  {category.name}
                </span>
                <svg data-component-id="src/components/common/ProductSidebar.tsx:258:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="258" data-component-file="ProductSidebar.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
                  width="16"
                  height="9"
                  viewBox="0 0 16 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`cursor-pointer transition-transform ${expandedCategories.includes(category.name) ? '' : '-rotate-90'
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCategoryToggle(category.name);
                  }}
                >
                  <path data-component-id="src/components/common/ProductSidebar.tsx:271:18" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="271" data-component-file="ProductSidebar.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                    d="M1 1L8 8L15 1"
                    stroke={category.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {expandedCategories.includes(category.name) && (
                <div data-component-id="src/components/common/ProductSidebar.tsx:281:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="281" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-0%20ml-4%20mb-2%22%7D" className="space-y-0 ml-4 mb-2">
                  {category.subMenuRoute?.map((subcategory) => (
                    <div data-component-id="src/components/common/ProductSidebar.tsx:283:20" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="283" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" key={subcategory.id} className="relative">
                      {/* Subcategoría con posible toggle */}
                      <div data-component-id="src/components/common/ProductSidebar.tsx:285:22" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="285" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20justify-between%22%7D" className="flex flex-row items-center justify-between">
                        <span data-component-id="src/components/common/ProductSidebar.tsx:286:24" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="286" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                          className={`cursor-pointer hover:text-global-2 transition-colors duration-200 ${isSubcategorySelected(subcategory) ? 'text-global-2 font-bold' : ''
                            }`}
                          style={{
                            fontFamily: "'TT_Norms_Pro-Medium', Helvetica",
                            fontSize: '14px',
                            lineHeight: '22px',
                            letterSpacing: '0%',
                            color: isSubcategorySelected(subcategory)
                              ? 'rgba(61, 28, 26, 1)'
                              : 'rgba(61, 28, 26, 0.6)', // #3D1C1A con 60% opacidad
                            fontWeight: isSubcategorySelected(subcategory) ? '700' : '500',
                          }}
                          onClick={() => handleSubcategoryClick(subcategory)}
                        >
                          {subcategory.name}
                        </span>
                        {/* Mostrar flecha solo si tiene sub-subcategorías */}
                        {subcategory.subMenuRoute && subcategory.subMenuRoute.length > 0 && (
                          <svg data-component-id="src/components/common/ProductSidebar.tsx:305:26" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="305" data-component-file="ProductSidebar.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
                            width="12"
                            height="7"
                            viewBox="0 0 12 7"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={`cursor-pointer transition-transform ml-2 ${expandedSubcategories.includes(subcategory.id.toString()) ? '' : '-rotate-90'
                              }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubcategoryToggle(subcategory.id.toString());
                            }}
                          >
                            <path data-component-id="src/components/common/ProductSidebar.tsx:318:28" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="318" data-component-file="ProductSidebar.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                              d="M1 1L6 6L11 1"
                              stroke="rgba(61, 28, 26, 0.6)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>

                      {/* Sub-subcategorías */}
                      {expandedSubcategories.includes(subcategory.id.toString()) &&
                        subcategory.subMenuRoute &&
                        subcategory.subMenuRoute.length > 0 && (
                          <div data-component-id="src/components/common/ProductSidebar.tsx:333:26" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="333" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-0%20ml-4%20mt-1%20mb-2%22%7D" className="space-y-0 ml-4 mt-1 mb-2">
                            {subcategory.subMenuRoute.map((subSubcategory) => (
                              <div data-component-id="src/components/common/ProductSidebar.tsx:335:30" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="335" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                                key={subSubcategory.id}
                                className={`cursor-pointer hover:text-global-2 transition-colors duration-200 ${isSubSubcategorySelected(subSubcategory) ? 'text-global-2 font-bold' : ''
                                  }`}
                                style={{
                                  fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                                  fontSize: '12px',
                                  lineHeight: '18px',
                                  letterSpacing: '0%',
                                  color: isSubSubcategorySelected(subSubcategory)
                                    ? 'rgba(61, 28, 26, 1)'
                                    : 'rgba(61, 28, 26, 0.5)', // #3D1C1A con 50% opacidad
                                  fontWeight: isSubSubcategorySelected(subSubcategory) ? '700' : '400',
                                }}
                                onClick={() => handleSubSubcategoryClick(subSubcategory)}
                              >
                                {subSubcategory.name}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Search by Brand - Dropdown */}
        <div data-component-id="src/components/common/ProductSidebar.tsx:365:8" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="365" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20mb-6%22%7D" className="mt-6 mb-6" ref={dropdownRef}>
          <div data-component-id="src/components/common/ProductSidebar.tsx:366:10" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="366" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
            <div data-component-id="src/components/common/ProductSidebar.tsx:367:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="367" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B10px%5D%20px-4%20py-3%20flex%20flex-row%20items-center%20gap-x-3%22%7D"
              className="rounded-[10px] px-4 py-3 flex flex-row items-center gap-x-3"
              style={{ backgroundColor: 'rgba(61, 28, 26, 0.17)' }}
            >
              <Image data-component-id="src/components/common/ProductSidebar.tsx:371:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="371" data-component-file="ProductSidebar.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_search.svg%22%2C%22alt%22%3A%22Search%22%7D" src="/images/img_search.svg" alt="Search" width={16} height={16} />
              <input data-component-id="src/components/common/ProductSidebar.tsx:372:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="372" data-component-file="ProductSidebar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchBrand%5D%22%2C%22className%22%3A%22flex-1%20bg-transparent%20border-none%20outline-none%20font-normal%20text-base%20leading-5%20text-%5B%238D766F%5D%20placeholder-%5B%238D766F%5D%22%7D"
                type="text"
                placeholder="Buscar por marca"
                value={searchBrand}
                onChange={(e) => {
                  setSearchBrand(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                className="flex-1 bg-transparent border-none outline-none font-normal text-base leading-5 text-[#8D766F] placeholder-[#8D766F]"
                style={{
                  fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                }}
              />
              {selectedManufacturer && (
                <button data-component-id="src/components/common/ProductSidebar.tsx:387:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="387" data-component-file="ProductSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-%5B%238D766F%5D%20hover%3Atext-%5B%233D1C1A%5D%20transition-colors%22%2C%22textContent%22%3A%22%C3%97%22%7D"
                  onClick={handleClearManufacturer}
                  className="text-[#8D766F] hover:text-[#3D1C1A] transition-colors"
                >
                  ×
                </button>
              )}
              <svg data-component-id="src/components/common/ProductSidebar.tsx:394:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="394" data-component-file="ProductSidebar.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
                width="16"
                height="9"
                viewBox="0 0 16 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`cursor-pointer transition-transform ${isDropdownOpen ? 'rotate-180' : ''
                  }`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <path data-component-id="src/components/common/ProductSidebar.tsx:404:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="404" data-component-file="ProductSidebar.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                  d="M1 1L8 8L15 1"
                  stroke="#3D1C1A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Dropdown de fabricantes */}
            {isDropdownOpen && (
              <div data-component-id="src/components/common/ProductSidebar.tsx:416:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="416" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20right-0%20mt-1%20max-h-48%20overflow-y-auto%20bg-%5B%23FEF5E6%5D%20rounded-%5B8px%5D%20border%20border-gray-200%20shadow-lg%20z-50%22%7D" className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-[#FEF5E6] rounded-[8px] border border-gray-200 shadow-lg z-50">
                {filteredManufacturers.length > 0 ? (
                  filteredManufacturers.map((manufacturer) => (
                    <div data-component-id="src/components/common/ProductSidebar.tsx:419:20" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="419" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20cursor-pointer%20hover%3Abg-%5B%233D1C1A%5D%20transition-colors%20group%22%7D"
                      key={manufacturer.id}
                      className="p-3 cursor-pointer hover:bg-[#3D1C1A] transition-colors group"
                      onClick={() => handleManufacturerSelect(manufacturer)}
                    >
                      <span data-component-id="src/components/common/ProductSidebar.tsx:424:22" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="424" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-normal%20text-sm%20leading-4%20text-%5B%233D1C1A%5D%20group-hover%3Atext-white%20transition-colors%22%7D" className="font-normal text-sm leading-4 text-[#3D1C1A] group-hover:text-white transition-colors">
                        {manufacturer.name}
                      </span>
                    </div>
                  ))
                ) : (
                  <div data-component-id="src/components/common/ProductSidebar.tsx:430:18" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="430" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20text-center%20text-%5B%238D766F%5D%22%7D" className="p-3 text-center text-[#8D766F]">
                    {searchBrand.trim() !== ''
                      ? 'No se encontraron fabricantes'
                      : 'No hay fabricantes disponibles'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fabricante seleccionado */}
          {selectedManufacturer && (
            <div data-component-id="src/components/common/ProductSidebar.tsx:442:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="442" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-3%22%7D" className="mt-3">
              <h4 data-component-id="src/components/common/ProductSidebar.tsx:443:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="443" data-component-file="ProductSidebar.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22font-bold%20text-sm%20leading-4%20text-%5B%233D1C1A%5D%20mb-2%22%2C%22textContent%22%3A%22Marca%20seleccionada%3A%22%7D" className="font-bold text-sm leading-4 text-[#3D1C1A] mb-2">
                Marca seleccionada:
              </h4>
              <div data-component-id="src/components/common/ProductSidebar.tsx:446:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="446" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-2%20bg-%5B%233D1C1A%5D%20text-white%20rounded-%5B6px%5D%22%7D" className="p-2 bg-[#3D1C1A] text-white rounded-[6px]">
                <span data-component-id="src/components/common/ProductSidebar.tsx:447:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="447" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-normal%20text-sm%20leading-4%22%7D" className="font-normal text-sm leading-4">{selectedManufacturer.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Price Filter */}
        <div data-component-id="src/components/common/ProductSidebar.tsx:454:8" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="454" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20relative%22%7D" className="mt-6 relative">
          {/* Vertical line that extends the full height */}
          <div data-component-id="src/components/common/ProductSidebar.tsx:456:10" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="456" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20left-0%20top-0%20w-1%20h-full%22%7D" className="absolute left-0 top-0 w-1 h-full" style={{ backgroundColor: 'rgba(61, 28, 26, 1)' }} />

          {/* Content with left margin to accommodate the line */}
          <div data-component-id="src/components/common/ProductSidebar.tsx:459:10" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="459" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22ml-4%22%7D" className="ml-4">
            {/* PRECIO title */}
            <h3 data-component-id="src/components/common/ProductSidebar.tsx:461:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="461" data-component-file="ProductSidebar.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-bold%20text-base%20leading-5%20text-%5B%233D1C1A%5D%20uppercase%20tracking-wide%20mb-4%22%2C%22textContent%22%3A%22PRECIO%22%7D"
              className="font-bold text-base leading-5 text-[#3D1C1A] uppercase tracking-wide mb-4"
              style={{
                fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              }}
            >
              PRECIO
            </h3>

            {/* Price input fields */}
            <div data-component-id="src/components/common/ProductSidebar.tsx:471:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="471" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20gap-x-2%20mb-4%22%7D" className="flex flex-row items-center gap-x-2 mb-4">
              <div data-component-id="src/components/common/ProductSidebar.tsx:472:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="472" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B8px%5D%20px-3%20py-2%20flex%20flex-row%20items-center%20gap-x-1%20flex-1%22%7D"
                className="rounded-[8px] px-3 py-2 flex flex-row items-center gap-x-1 flex-1"
                style={{ backgroundColor: 'rgba(61, 28, 26, 0.17)' }}
              >
                <input data-component-id="src/components/common/ProductSidebar.tsx:476:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="476" data-component-file="ProductSidebar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3AminPrice%5D%22%2C%22className%22%3A%22flex-1%20bg-transparent%20border-none%20outline-none%20font-normal%20text-base%20leading-5%20text-%5B%238D766F%5D%20text-center%20%5Bappearance%3Atextfield%5D%20%5B%26%3A%3A-webkit-outer-spin-button%5D%3Aappearance-none%20%5B%26%3A%3A-webkit-inner-spin-button%5D%3Aappearance-none%22%7D"
                  type="number"
                  value={minPrice}
                  onChange={(e) => handleMinPriceChange(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none font-normal text-base leading-5 text-[#8D766F] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                  }}
                  min="0"
                  max="200"
                />
                <span data-component-id="src/components/common/ProductSidebar.tsx:487:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="487" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-normal%20text-base%20leading-5%20text-%5B%238D766F%5D%22%2C%22textContent%22%3A%22%E2%82%AC%22%7D"
                  className="font-normal text-base leading-5 text-[#8D766F]"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                  }}
                >
                  €
                </span>
              </div>
              <div data-component-id="src/components/common/ProductSidebar.tsx:496:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="496" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%22%7D" className="flex items-center justify-center">
                <span data-component-id="src/components/common/ProductSidebar.tsx:497:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="497" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%238D766F%5D%20font-normal%22%2C%22textContent%22%3A%22-%22%7D" className="text-[#8D766F] font-normal">-</span>
              </div>
              <div data-component-id="src/components/common/ProductSidebar.tsx:499:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="499" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B8px%5D%20px-3%20py-2%20flex%20flex-row%20items-center%20gap-x-1%20flex-1%22%7D"
                className="rounded-[8px] px-3 py-2 flex flex-row items-center gap-x-1 flex-1"
                style={{ backgroundColor: 'rgba(61, 28, 26, 0.17)' }}
              >
                <input data-component-id="src/components/common/ProductSidebar.tsx:503:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="503" data-component-file="ProductSidebar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3AmaxPrice%5D%22%2C%22className%22%3A%22flex-1%20bg-transparent%20border-none%20outline-none%20font-normal%20text-base%20leading-5%20text-%5B%238D766F%5D%20text-center%20%5Bappearance%3Atextfield%5D%20%5B%26%3A%3A-webkit-outer-spin-button%5D%3Aappearance-none%20%5B%26%3A%3A-webkit-inner-spin-button%5D%3Aappearance-none%22%7D"
                  type="number"
                  value={maxPrice}
                  onChange={(e) => handleMaxPriceChange(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none font-normal text-base leading-5 text-[#8D766F] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                  }}
                  min="0"
                  max="200"
                />
                <span data-component-id="src/components/common/ProductSidebar.tsx:514:16" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="514" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-normal%20text-base%20leading-5%20text-%5B%238D766F%5D%22%2C%22textContent%22%3A%22%E2%82%AC%22%7D"
                  className="font-normal text-base leading-5 text-[#8D766F]"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                  }}
                >
                  €
                </span>
              </div>
            </div>

            {/* Price Range Slider */}
            <div data-component-id="src/components/common/ProductSidebar.tsx:526:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="526" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mb-2%22%7D" className="relative mb-2">
              <Range data-component-id="src/components/common/ProductSidebar.tsx:527:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="527" data-component-file="ProductSidebar.tsx" data-component-name="Range" data-component-content="%7B%22elementName%22%3A%22Range%22%7D"
                step={1}
                min={0}
                max={200}
                values={priceRange}
                onChange={(values) => {
                  setPriceRange([values[0], values[1]]);
                  setMinPrice(values[0].toString());
                  setMaxPrice(values[1].toString());
                }}
                renderTrack={({ props, children }) => (
                  <div data-component-id="src/components/common/ProductSidebar.tsx:538:18" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="538" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22className%22%3A%22flex-1%20h-%5B2px%5D%20bg-%5B%238D766F%5D%20relative%20rounded-full%22%7D"
                    {...props}
                    className="flex-1 h-[2px] bg-[#8D766F] relative rounded-full"
                    style={{
                      ...props.style,
                    }}
                  >
                    <div data-component-id="src/components/common/ProductSidebar.tsx:545:20" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="545" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20h-full%20bg-%5B%238D766F%5D%20rounded-full%22%7D"
                      className="absolute top-0 h-full bg-[#8D766F] rounded-full"
                      style={{
                        left: `${(priceRange[0] / 200) * 100}%`,
                        width: `${((priceRange[1] - priceRange[0]) / 200) * 100}%`,
                      }}
                    />
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => {
                  const { key, ...otherProps } = props;
                  return (
                    <div data-component-id="src/components/common/ProductSidebar.tsx:558:20" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="558" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22className%22%3A%22w-3%20h-3%20bg-%5B%238D766F%5D%20rounded-full%20cursor-pointer%20hover%3Ascale-110%20transition-transform%22%7D"
                      key={key}
                      {...otherProps}
                      className="w-3 h-3 bg-[#8D766F] rounded-full cursor-pointer hover:scale-110 transition-transform"
                      style={{
                        ...otherProps.style,
                      }}
                    />
                  );
                }}
              />
            </div>

            {/* Range labels */}
            <div data-component-id="src/components/common/ProductSidebar.tsx:572:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="572" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20justify-between%22%7D" className="flex flex-row justify-between">
              <span data-component-id="src/components/common/ProductSidebar.tsx:573:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="573" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-normal%20text-sm%20leading-4%20text-%5B%238D766F%5D%22%2C%22textContent%22%3A%220%22%7D"
                className="font-normal text-sm leading-4 text-[#8D766F]"
                style={{
                  fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                }}
              >
                0
              </span>
              <span data-component-id="src/components/common/ProductSidebar.tsx:581:14" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="581" data-component-file="ProductSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-normal%20text-sm%20leading-4%20text-%5B%238D766F%5D%22%2C%22textContent%22%3A%22200%22%7D"
                className="font-normal text-sm leading-4 text-[#8D766F]"
                style={{
                  fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                }}
              >
                200
              </span>
            </div>
          </div>
        </div>

        {/* Botón de filtrar */}
        {hasActiveFilters && (
          <div data-component-id="src/components/common/ProductSidebar.tsx:595:10" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="595" data-component-file="ProductSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%22%7D" className="mt-6">
            <button data-component-id="src/components/common/ProductSidebar.tsx:596:12" data-component-path="src/components/common/ProductSidebar.tsx" data-component-line="596" data-component-file="ProductSidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20bg-%5B%233D1C1A%5D%20text-white%20py-3%20px-4%20rounded-%5B10px%5D%20font-bold%20text-base%20leading-5%20hover%3Abg-%5B%232A1422%5D%20transition-colors%22%2C%22textContent%22%3A%22Filtrar%22%7D"
              onClick={handleSearchByFilters}
              className="w-full bg-[#3D1C1A] text-white py-3 px-4 rounded-[10px] font-bold text-base leading-5 hover:bg-[#2A1422] transition-colors"
              style={{
                fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              }}
            >
              Filtrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSidebar;
